import { escapeFilterValue } from '../../lib/pocketbase'
/**
 * Deal reads go through /api/portal/deals, not PocketBase.
 *
 * PocketBase has no per-field rules: asking it for fewer columns decided what
 * the portal showed, not what the athlete's token could fetch, and deals carry
 * the agency's floor and target values, notes and commission. The server
 * shapes each deal (shared/portalDeal.ts) and the deals collection no longer
 * answers a portal token. Screens that name a deal (tasks, messages,
 * documents) take the title from dealTitle(), loaded from the same route,
 * because a relation expand of a deal now comes back empty.
 *
 * Activities still read directly: their row rule already limits them to the
 * athlete's own deals and client-safe types.
 */
import { getClientPb } from '../../lib/clientPocketbase'
import { getClientToken, getClientUser } from '../clientAuth'
import type { ClientDealView, ClientDealActivity, ClientPaymentView } from '../../types/clientPortal'
import type { PortalDeal } from '../../shared/portalDeal'
import { portalRoutePath } from './previewMode'

async function portalDeals(query = ''): Promise<any> {
  const token = getClientToken()
  if (!token) throw new Error('Client not authenticated')
  const res = await fetch(`${portalRoutePath('deals')}${query}`, { headers: { Authorization: token } })
  const data = await res.json().catch(() => null)
  if (!res.ok) throw new Error(data?.error || `Your deals could not be loaded (${res.status}).`)
  return data
}

const toView = (d: PortalDeal): ClientDealView => ({ ...d, stage: d.stage as ClientDealView['stage'] })

// ── Titles for screens that name a deal ──
let titles = new Map<string, string>()
let loaded: { at: number; promise: Promise<Map<string, string>> } | null = null

/** The athlete's deal titles by id, fetched at most once a minute. */
export function loadClientDealTitles(): Promise<Map<string, string>> {
  if (loaded && Date.now() - loaded.at < 60_000) return loaded.promise
  const promise = portalDeals().then(({ deals }) => {
    titles = new Map((deals as PortalDeal[]).map(d => [d.id, d.title]))
    return titles
  })
  loaded = { at: Date.now(), promise }
  promise.catch(() => { loaded = null })
  return promise
}

/** A deal's title from the last load, for a record that points at it. */
export function dealTitle(dealId: string | null | undefined): { id: string; title: string } | undefined {
  if (!dealId) return undefined
  const title = titles.get(dealId)
  return title == null ? undefined : { id: dealId, title }
}

export async function getClientDeals(): Promise<ClientDealView[]> {
  const { deals } = await portalDeals()
  titles = new Map((deals as PortalDeal[]).map(d => [d.id, d.title]))
  return (deals as PortalDeal[]).map(toView)
}

/** The agents who own the athlete's deals (ids), for the "your team" list. */
export async function getClientDealAgentIds(): Promise<string[]> {
  const { agent_ids } = await portalDeals()
  if (!Array.isArray(agent_ids)) throw new Error('Your deals came back without their agents.')
  return agent_ids
}

export async function getClientDealDetail(dealId: string): Promise<ClientDealView | null> {
  const { deal } = await portalDeals(`?id=${encodeURIComponent(dealId)}`)
  return deal ? toView(deal as PortalDeal) : null
}

export const CLIENT_SAFE_ACTIVITY_TYPES = [
  'deal_created',
  'deal_updated',
  'deal_stage_changed',
  'contract_created',
  'contract_signed',
  'document_uploaded',
  'payment_received',
]

export async function getClientDealActivities(dealId: string): Promise<ClientDealActivity[]> {
  const user = getClientUser()
  if (!user) throw new Error('Client not authenticated')

  // The activities rule limits rows to this athlete's own deals.
  const records = await getClientPb().collection('activities').getFullList({
    filter: `deal_id = "${escapeFilterValue(dealId)}"`,
    sort: '-created',
    fields: 'id,activity_type,title,description,created',
  })

  return records
    .filter((r) => CLIENT_SAFE_ACTIVITY_TYPES.includes(r.activity_type))
    .map((r) => ({
      id: r.id,
      type: r.activity_type as ClientDealActivity['type'],
      title: r.title,
      description: r.description,
      created: r.created,
    }))
}

/**
 * The deal's payment schedule, as the agency keeps it on the deal. Amounts
 * are null where the agency does not share this deal's value.
 */
export async function getClientDealPayments(dealId: string): Promise<ClientPaymentView[]> {
  const { deal } = await portalDeals(`?id=${encodeURIComponent(dealId)}`)
  if (!deal) throw new Error('Access denied')
  return (deal as PortalDeal).payments
}
